/**
 * DedroneIntegrationsList — collapsed list of DroneTracker servers on the
 * Sensors page. Phase 10 moves Dedrone integration management out of the
 * Settings page so multiple instances live alongside the sub-sensors each
 * one auto-discovers.
 *
 * Each card shows label, status pill (active / disabled / error), last poll
 * + last webhook ages, and a copy-webhook button. Clicking the card opens
 * an inline editor for URL / token / poll interval / enabled. Delete is
 * confirmed inline (no separate modal).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ShieldCheckIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { dedroneApi, type DedroneIntegration, type DedroneIntegrationInput } from '../../services/dedroneApi';
import { SkeletonCard } from '../common/Skeleton';

function ageString(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'unknown';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function DedroneIntegrationsList() {
  const query = useQuery({
    queryKey: ['dedrone-integrations'],
    queryFn: dedroneApi.list,
    refetchInterval: 15_000, // freshen "last webhook 12s ago" without manual reload
  });

  if (query.isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  const integrations = query.data ?? [];
  if (integrations.length === 0) return null; // hide the section entirely until the first one exists

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ShieldCheckIcon className="h-5 w-5 text-accent" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400">
          DeDrone DroneTracker Servers
        </h2>
        <span className="text-xs text-gray-500">{integrations.length}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {integrations.map((it) => (
          <IntegrationCard key={it.id} integration={it} />
        ))}
      </div>
    </div>
  );
}

function IntegrationCard({ integration }: { integration: DedroneIntegration }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // Form state for the inline editor. Token is write-only — empty means
  // "keep existing." baseUrl / pollSeconds / enabled / label are hydrated
  // from the row each time the card is expanded.
  const [label, setLabel] = useState(integration.label);
  const [baseUrl, setBaseUrl] = useState(integration.baseUrl);
  const [apiToken, setApiToken] = useState('');
  const [pollSeconds, setPollSeconds] = useState(integration.pollIntervalSeconds);
  const [enabled, setEnabled] = useState(integration.enabled);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: DedroneIntegrationInput = {
        label: label.trim() || undefined,
        baseUrl: baseUrl.trim().replace(/\/+$/, ''),
        pollIntervalSeconds: pollSeconds,
        enabled,
      };
      if (apiToken) payload.apiToken = apiToken;
      return dedroneApi.update(integration.id, payload);
    },
    onSuccess: () => {
      toast.success('Integration updated');
      setApiToken('');
      setExpanded(false);
      queryClient.invalidateQueries({ queryKey: ['dedrone-integrations'] });
    },
    onError: (err: Error) => toast.error(err.message || 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => dedroneApi.remove(integration.id),
    onSuccess: () => {
      toast.success('Integration removed');
      queryClient.invalidateQueries({ queryKey: ['dedrone-integrations'] });
    },
    onError: (err: Error) => toast.error(err.message || 'Delete failed'),
  });

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(integration.webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const statusColor = !integration.enabled
    ? 'bg-gray-500 text-gray-300'
    : integration.lastError
      ? 'bg-alert/20 text-alert'
      : 'bg-accent/20 text-accent';
  const statusLabel = !integration.enabled ? 'Disabled' : integration.lastError ? 'Error' : 'Active';

  return (
    <div className="glass-card p-4">
      {/* Header row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="p-2 rounded-lg bg-accent/10 text-accent flex-shrink-0">
            <ShieldCheckIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-white truncate">{integration.label}</h3>
            <p className="text-[11px] text-gray-500 truncate font-mono">{integration.baseUrl}</p>
          </div>
        </div>
        <span className={clsx('text-[10px] px-2 py-1 rounded-full font-semibold flex-shrink-0', statusColor)}>
          {statusLabel}
        </span>
      </div>

      {/* Metrics row */}
      <dl className="grid grid-cols-3 gap-2 text-[11px] mb-3">
        <div>
          <dt className="text-gray-500 uppercase tracking-wider">Last poll</dt>
          <dd className="font-mono text-gray-300 mt-0.5">{ageString(integration.lastPulledAt)}</dd>
        </div>
        <div>
          <dt className="text-gray-500 uppercase tracking-wider">Last webhook</dt>
          <dd className="font-mono text-gray-300 mt-0.5">{ageString(integration.lastWebhookAt)}</dd>
        </div>
        <div>
          <dt className="text-gray-500 uppercase tracking-wider">Poll every</dt>
          <dd className="font-mono text-gray-300 mt-0.5">{integration.pollIntervalSeconds}s</dd>
        </div>
      </dl>

      {integration.lastError && (
        <div className="flex items-start gap-2 p-2 bg-alert/5 border border-alert/20 rounded-md text-[11px] text-alert mb-3">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div className="font-mono break-words">
            <span className="opacity-70">{ageString(integration.lastErrorAt)} · </span>
            {integration.lastError}
          </div>
        </div>
      )}

      {/* Webhook URL — copyable */}
      <div className="flex items-center gap-2 p-2 bg-navy-900 rounded-md border border-navy-700 mb-3">
        <code className="text-[11px] text-gray-300 font-mono flex-1 break-all">{integration.webhookUrl}</code>
        <button
          onClick={copyWebhookUrl}
          className="flex-shrink-0 text-gray-500 hover:text-accent transition-colors"
          title="Copy webhook URL"
        >
          {copied ? <CheckIcon className="h-4 w-4 text-accent" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
        </button>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-navy-700/50">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-[12px] text-accent hover:text-accent-light flex items-center gap-1"
        >
          {expanded ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
          {expanded ? 'Hide settings' : 'Configure'}
        </button>
        <button
          onClick={() => {
            if (confirm(`Remove "${integration.label}"? Sub-sensors already discovered will remain.`)) {
              deleteMutation.mutate();
            }
          }}
          disabled={deleteMutation.isPending}
          className="text-[12px] text-gray-500 hover:text-alert flex items-center gap-1"
        >
          <TrashIcon className="h-3.5 w-3.5" />
          Remove
        </button>
      </div>

      {/* Inline editor */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-navy-700/50 space-y-3">
          <EditorField label="Label">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-md text-white text-sm focus:outline-none focus:border-accent"
            />
          </EditorField>
          <EditorField label="DroneTracker URL">
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-md text-white text-sm focus:outline-none focus:border-accent"
            />
          </EditorField>
          <EditorField label="New Auth Token (leave empty to keep existing)">
            <input
              type="password"
              placeholder="••••••••••••"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-md text-white text-sm font-mono focus:outline-none focus:border-accent"
            />
          </EditorField>
          <div className="grid grid-cols-2 gap-3">
            <EditorField label="Poll Interval (s)">
              <input
                type="number"
                min={15}
                max={3600}
                value={pollSeconds}
                onChange={(e) => setPollSeconds(parseInt(e.target.value, 10) || 60)}
                className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-md text-white text-sm focus:outline-none focus:border-accent"
              />
            </EditorField>
            <label className="flex items-end gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-navy-600 bg-navy-800 text-accent focus:ring-accent/30 mb-2.5"
              />
              <span className="text-xs text-gray-300 mb-2">Enabled</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setExpanded(false)}
              className="btn-secondary text-xs"
              disabled={saveMutation.isPending}
            >
              Cancel
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="btn-primary text-xs"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EditorField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium uppercase tracking-wider text-gray-400 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
